import { type ColumnDef, type Row } from '@tanstack/react-table';
import { type LucideIcon } from 'lucide-react';
import { AssetThumbnail } from '@/components/asset-thumbnail';
import { ColumnType } from '@/types/column';
import { Variants } from '@/types/asset';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface ThumbnailColumnOptions<T> {
  /** Unique column ID (defaults to "thumbnail") */
  id?: string;

  /**
   * Return a URL string that we’ll pass as the `thumbnail` prop
   * (takes precedence over variantKey)
   */
  getThumbnail?: (row: Row<T>) => string | undefined;

  /** Which variant to pass to AssetThumbnail */
  variantKey?: Variants;

  /** Pick the type string */
  getType?: (row: Row<T>) => string | undefined;

  /** Overlay icon component (e.g. Trash2, Hourglass) or undefined */
  getOverlayIcon?: (row: Row<T>) => LucideIcon | undefined;

  /** Static props */
  size?: number;
  width?: number;
  height?: number;
  draggable?: boolean;

  /** Hooks to customize each computed className */
  imageContainerClassNameFn?: (row: Row<T>) => string;
  imageClassNameFn?: (row: Row<T>, isListView: boolean) => string;
  iconContainerClassNameFn?: (row: Row<T>) => string;
  iconClassNameFn?: (row: Row<T>) => string;
  overlayIconClassNameFn?: (row: Row<T>) => string;

  galleryView: GalleryView;
  thumbnailSize: number;

  cellClassName?: string;
  headerClassName?: string;

  dataCy?: string;

  getDisablePolling?: (row: Row<T>) => boolean | undefined;
}

export function ThumbnailColumn<T extends Record<string, any>>({
  id = ColumnType.Thumbnail,
  getThumbnail,
  variantKey = Variants.ThumbnailInternal,
  getType,
  getOverlayIcon,
  size = 75,
  width = 150,
  height = 150,
  draggable = false,
  cellClassName,
  headerClassName,
  galleryView,
  thumbnailSize,
  imageContainerClassNameFn,
  imageClassNameFn,
  iconContainerClassNameFn,
  iconClassNameFn,
  overlayIconClassNameFn,
  dataCy,
  getDisablePolling,
}: ThumbnailColumnOptions<T>): ColumnDef<T> {
  return {
    id,
    accessorKey: 'thumbnail',
    header: '',
    meta: {
      cellClassName: cn(
        'p-0! relative min-w-12 max-w-12 w-12',
        galleryView === GalleryView.LIST && 'h-px',
        cellClassName,
      ),
      headerClassName: cn('min-w-12 max-w-12 w-12', headerClassName),
    },

    size,
    cell: ({ row }: { row: Row<T> }) => {
      const data = row.original as any;
      const isListView = galleryView === GalleryView.LIST;
      const typeStr = getType ? getType(row) : (data.currentVersion?.mimeType ?? data.currentVersion?.fileType);
      const OverlayIcon = getOverlayIcon?.(row);

      const iconSizes = !isListView
        ? thumbnailSize === 140
          ? 'icon-10'
          : thumbnailSize === 180
            ? 'icon-12'
            : thumbnailSize === 220
              ? 'icon-14'
              : thumbnailSize === 260
                ? 'icon-16'
                : 'icon-20'
        : 'icon-6';

      const overlayIconClassName = !isListView
        ? thumbnailSize === 140
          ? 'icon-8'
          : thumbnailSize === 180
            ? 'icon-10'
            : thumbnailSize === 220
              ? 'icon-12'
              : thumbnailSize === 260
                ? 'icon-14'
                : 'icon-16'
        : 'icon-6';

      return (
        <AssetThumbnail
          thumbnail={getThumbnail ? getThumbnail(data) : data.variants?.[variantKey]?.url}
          id={data.id}
          asset={data}
          variantKey={variantKey}
          type={typeStr}
          draggable={draggable}
          width={width}
          height={height}
          overlayIcon={OverlayIcon}
          isListView={isListView}
          {...(getDisablePolling && { disablePolling: getDisablePolling(data) })}
          imageContainerClassName={cn(
            'bg-ui-panel',
            isListView ? 'absolute inset-0' : 'relative',
            imageContainerClassNameFn?.(data),
          )}
          imageClassName={cn(
            'w-full h-full min-h-full min-w-full transition-all object-contain',
            {
              'aspect-square': !isListView,
              'opacity-50': data.trashed,
            },
            imageClassNameFn?.(data, isListView),
          )}
          iconContainerClassName={cn(
            'flex w-full items-center justify-center bg-ui-panel',
            isListView ? 'h-full' : 'h-auto aspect-square',
            {
              'bg-ui-light': isListView && row.getIsSelected(),
            },
            iconContainerClassNameFn?.(data),
          )}
          iconClassName={cn(
            'stroke-ui-light',
            iconSizes,
            {
              'stroke-text-gray-alternate!': isListView && row.getIsSelected(),
            },
            iconClassNameFn?.(data),
          )}
          playIconClassName={cn({ 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2': isListView })}
          overlayIconClassName={cn(overlayIconClassName, overlayIconClassNameFn?.(data))}
          dataCy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${id}`}
          htmlImage
        />
      );
    },
  };
}
